import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Typography, TextField, Checkbox, FormControlLabel, Button, Link } from '@mui/material';
import { Card } from 'components/Card';
import { InfoMessage } from 'components/InfoMessage';
import { Ga4ButtonDefault, Ga4ButtonError, Ga4ButtonSuccess, GA4CheckList } from 'components/GA4Parts';
import { BillingMethod, CampaignFields, RouteList } from 'types';
import { getAnalyticsAccountChecklist } from 'shared';
import { useCampaignForm } from 'hooks';
import { CopyText } from 'components/CopyText';
import { numberInputStyling } from 'shared/ui';
import { EditOutlined } from '@mui/icons-material';

export const GoogleAnalytics = () => {
  const {
    getFieldControls,
    watch,
    setValue,
    setFocus,
    formState: { errors },
    failureCount,
    isConnectionSuccess,
    setIsConnectionSuccess,
    checkGa4ConnectionMutation,
    getGa4CampaignUrlQuery,
    isConnectionSuccessCampaignState,
  } = useCampaignForm();

  const isClickBillingMethodSelected = watch(CampaignFields.billingMethod) === BillingMethod.CLICK;
  const ga4PropertyId = watch(CampaignFields.ga4PropertyId);
  const [isEditing, setIsEditing] = useState(false);
  const shouldShowForm = isEditing || !ga4PropertyId;

  const configUrl = getGa4CampaignUrlQuery.data?.url;

  const checkGa4Connection = useCallback(() => {
    checkGa4ConnectionMutation.mutate({ sendSuccessEmail: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ignore mutation identity changes
  }, [checkGa4ConnectionMutation.mutate]);

  const GA4Config = useMemo(() => {
    const PropertyIdInput = () => (
      <TextField
        autoComplete="off"
        size="small"
        label="Property-ID angeben"
        variant="outlined"
        type="number"
        sx={{
          width: { xs: 1, md: 1 / 3 },
          mb: 2,
          ...numberInputStyling,
          '& .MuiInputBase-input.MuiInputBase-input': { background: '#ffffff' },
        }}
        error={!!errors.ga4PropertyId?.message}
        helperText={errors.ga4PropertyId?.message}
        {...getFieldControls(CampaignFields.ga4PropertyId, {
          setValueAs: (value: string) => {
            setIsEditing(true);
            return value.trim();
          },
          validate: async (val) => {
            if (isClickBillingMethodSelected) return true;
            if (!val) {
              setFocus(CampaignFields.ga4PropertyId);
              return 'Gib bitte die Property-ID ein';
            }
            if (isClickBillingMethodSelected || (!!val && isConnectionSuccessCampaignState)) {
              return true;
            }
            return false;
          },
        })}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (['-', 'e', 'E', '+', '.'].includes(e.key)) {
            e.preventDefault();
          }
        }}
      />
    );
    return getAnalyticsAccountChecklist({
      checkGa4ConnectionMutation: checkGa4Connection,
      shouldShowAdditionalError: !!ga4PropertyId && isConnectionSuccess === false,
      PropertyIdInput,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkGa4Connection,
    isClickBillingMethodSelected,
    isConnectionSuccessCampaignState,
    isConnectionSuccess,
    setFocus,
  ]);

  const CheckStateMessages = () => {
    return (
      <>
        {isConnectionSuccess === false &&
          (failureCount >= 2 ? (
            <InfoMessage
              fullWidth
              variant="error"
              title="Verbindung erneut fehlgeschlagen - Weiter ohne GA4?"
              message={
                <>
                  <Typography>
                    Wenn du ohne GA4 fortfahren möchtest, wähle das Preismodell “Preis pro Klick” anstelle von “Preis
                    pro Engagement”.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isClickBillingMethodSelected}
                        onChange={(e) =>
                          setValue(
                            CampaignFields.billingMethod,
                            e.target.checked ? BillingMethod.CLICK : BillingMethod.ENGAGEMENT
                          )
                        }
                      />
                    }
                    label="Weiter mit “Preis pro Klick” anstatt “Preis pro Engagement” um GA4-Verbindung zu umgehen."
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontWeight: 700,
                      },
                    }}
                  />
                </>
              }
            />
          ) : (
            <InfoMessage
              fullWidth
              variant="error"
              title="Verbindung fehlgeschlagen"
              message={
                <Typography sx={{ color: 'inherit' }}>
                  Ooops, etwas ist schiefgelaufen. Überprüfe die Schritte oben und versuch es nochmals. <br />
                  Benötigst du Unterstützung?{' '}
                  <Link href={RouteList.Contact} color="inherit" target="_blank">
                    Hier findest du weitere Hilfe.
                  </Link>
                </Typography>
              }
            />
          ))}
        {isConnectionSuccess === true && (
          <InfoMessage
            fullWidth
            variant="success"
            title="Super, die Verbindung hat geklappt!"
            message={<Typography>Gehe weiter zum nächsten Schritt.</Typography>}
          />
        )}
      </>
    );
  };

  useEffect(() => {
    setIsConnectionSuccess(null);
  }, [setIsConnectionSuccess]);

  return (
    <Stack gap={6}>
      <Typography variant="subtitle1" fontWeight={500} lineHeight="28px">
        Google Analytics 4 mit Alloy verbinden
      </Typography>
      {shouldShowForm ? (
        <>
          <InfoMessage
            fullWidth
            variant="info"
            title="Du hast keinen GA4-Zugang oder Adminrechte?"
            message={
              <>
                <Typography variant="body2">
                  Sende folgenden Link an die zuständige Person in deinem Unternehmen. Sie kann die Verbindung für dich
                  herstellen.{' '}
                </Typography>
                <CopyText textToCopy={configUrl || ''} small isLink />
              </>
            }
          />
          <GA4CheckList checkList={GA4Config} />
          <CheckStateMessages />
        </>
      ) : (
        <Stack gap={6} width={{ xs: 1, lg: 2 / 3 }}>
          {isConnectionSuccessCampaignState ? (
            <InfoMessage
              fullWidth
              variant="success"
              title="Dein GA4-Konto ist bereits mit Alloy verbunden!"
              message={
                <Typography variant="body2">Fahre fort, wenn die untenstehende Property-ID korrekt ist.</Typography>
              }
            />
          ) : (
            <InfoMessage
              fullWidth
              variant="error"
              title="Verbindungsfehler zwischen deinem GA4-Konto und Alloy"
              message={
                <Typography variant="body2">
                  Dein GA4-Konto war bereits mit Alloy verbunden, aber es gibt ein Verbindungsproblem. Bitte überprüfe
                  deine GA4-Einstellungen. Benötigst du Unterstützung?{' '}
                  <Link href={RouteList.Contact} color="inherit" target="_blank">
                    Hier findest du Hilfe
                  </Link>
                  .
                </Typography>
              }
            />
          )}
          <Card>
            <Stack gap={2}>
              <Typography variant="h4" pb={2}>
                Deine GA4 Property-ID lautet
              </Typography>
              <Typography variant="h4">{ga4PropertyId}</Typography>
              <Stack
                direction="row"
                gap={2}
                sx={({ spacing }) => ({
                  '& .MuiButtonBase-root': {
                    fontSize: spacing(3),
                    height: spacing(7),
                    px: spacing(2),
                    py: spacing(1),
                    lineHeight: 1.2,
                  },
                  '& .MuiButton-startIcon': { marginRight: spacing(2) },
                  '& .MuiSvgIcon-root.MuiSvgIcon-fontSizeMedium': { fontSize: spacing(4) },
                })}
              >
                {isConnectionSuccess === null ? (
                  <Ga4ButtonDefault onClick={() => checkGa4ConnectionMutation.mutate({ sendSuccessEmail: false })} />
                ) : isConnectionSuccess ? (
                  <Ga4ButtonSuccess />
                ) : (
                  <Ga4ButtonError />
                )}
                <Button variant="text" startIcon={<EditOutlined />} onClick={() => setIsEditing(true)}>
                  Property id ändern
                </Button>
              </Stack>
              {isConnectionSuccess === false && (
                <Typography
                  sx={({ spacing }) => ({
                    color: 'error.dark',
                    width: 1 / 2.5,
                    fontWeight: 400,
                    fontSize: spacing(3),
                    lineHeight: '150%',
                  })}
                >
                  Überprüfe bitte die Einstellungen in deinem GA4-Konto. Benötigst du Unterstützung?{' '}
                  <Link href={RouteList.Contact} color="inherit" target="_blank">
                    Hier findest du Hilfe
                  </Link>
                  .
                </Typography>
              )}
            </Stack>
          </Card>
        </Stack>
      )}
    </Stack>
  );
};
